import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from './app-context';
import { sendChatMessage, loadChatConversation, startNewChatConversation } from './api';
import styles from './llm-chat.module.css';
import tinyLogo from '../logos/tiny1.png';

const LLMChat = ({ status, onClose }) => {
    const { spaceScopedFetch, setAgentFilter } = useAppContext();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if(status === "open") {
            scrollToBottom();
        }
    }, [messages]);

    useEffect(() => {
        if (status === "open" && inputRef.current) {
            inputRef.current.focus();
        }
    }, [status]);

    // Load conversation history when panel opens
    useEffect(() => {
        if (spaceScopedFetch) {
            loadChatConversation(spaceScopedFetch)
                .then(data => setMessages(data.conversation || []))
                .catch(err => {
                    console.error('Failed to load conversation:', err);
                    setError('Failed to load conversation history.');
                });
        }
    }, [spaceScopedFetch]);

    const sendMessage = async (messageText) => {
        const userMessage = messageText.trim();
        if (!userMessage || isLoading) return;

        setInput('');
        setError(null);
        setIsLoading(true);

        // Optimistically add user message to UI immediately
        const optimisticMessage = {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };
        setMessages(prevMessages => [...prevMessages, optimisticMessage]);

        try {
            const response = await sendChatMessage(spaceScopedFetch, userMessage);
            // Replace with server response (includes assistant's reply)
            setMessages(response.conversation || []);
            if (response.transactionFilter) {
                setAgentFilter(response.transactionFilter);
                navigate('/app/transactions');
            }
        } catch (err) {
            console.error('Chat error:', err);
            setError('Failed to send message. Please try again.');
            // Remove optimistic message on error
            setMessages(prevMessages => prevMessages.slice(0, -1));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendClick = () => {
        sendMessage(input);
    };

    const handleNewConversation = async () => {
        if (isLoading) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await startNewChatConversation(spaceScopedFetch);
            setMessages(response.conversation || []);
        } catch (err) {
            console.error('Failed to start new conversation:', err);
            setError('Failed to start new conversation.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input);
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.chatHeader}>
                <div className={styles.chatTitle}>
                    <h2>Financial Assistant</h2>
                </div>
                <div className={styles.headerActions}>
                    <button 
                        className={styles.newChatButton}
                        onClick={handleNewConversation}
                        disabled={isLoading}
                        aria-label="New conversation"
                        title="Start new conversation"
                    >
                        <span className="material-icons">refresh</span>
                    </button>
                    <button 
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close chat"
                    >
                        <span className="material-icons">close</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className={styles.errorBanner}>
                    <span className="material-icons">error</span>
                    {error}
                </div>
            )}

            <div className={styles.chatMessages}>
                {messages.length === 0 && (
                    <div className={styles.welcomeMessage}>
                        <p>Hi! I'm your financial assistant.</p>
                        <p>Ask me questions about your transactions, spending patterns, or categories.</p>
                        <div className={styles.exampleQuestions}>
                            <p><strong>Try asking:</strong></p>
                            <ul>
                                <li>
                                    <button 
                                        className={styles.exampleLink}
                                        onClick={() => sendMessage("Show me my largest transactions")}
                                        disabled={isLoading}
                                    >
                                        "Show me my largest transactions"
                                    </button>
                                </li>
                                <li>
                                    <button 
                                        className={styles.exampleLink}
                                        onClick={() => sendMessage("What did I spend on groceries this month?")}
                                        disabled={isLoading}
                                    >
                                        "What did I spend on groceries this month?"
                                    </button>
                                </li>
                                <li>
                                    <button 
                                        className={styles.exampleLink}
                                        onClick={() => sendMessage("Find transactions over £100")}
                                        disabled={isLoading}
                                    >
                                        "Find transactions over £100"
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

                {messages.map((message, index) => (
                    <MessageBubble key={index} message={message} />
                ))}

                {isLoading && (
                    <div className={styles.typingIndicator}>
                        <div className={styles.dot}></div>
                        <div className={styles.dot}></div>
                        <div className={styles.dot}></div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className={styles.chatInput}>
                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask a question about your finances..."
                    rows="1"
                    disabled={isLoading}
                />
                <button 
                    onClick={handleSendClick}
                    disabled={!input.trim() || isLoading}
                    aria-label="Send message"
                >
                    <span className="material-icons">send</span>
                </button>
            </div>
        </div>
    );
};

const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    const timestamp = message.timestamp ? new Date(message.timestamp) : null;
    
    return (
        <div className={`${styles.messageBubble} ${isUser ? styles.userMessage : styles.assistantMessage}`}>
            <div className={styles.messageHeader}>
                {isUser ? (
                    <span className="material-icons">person</span>
                ) : (
                    <img src={tinyLogo} alt="Budget Butler" className={styles.assistantIcon} />
                )}
                {timestamp && (
                    <span className={styles.messageTime}>
                        {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
            </div>
            <div className={styles.messageContent}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
            </div>
        </div>
    );
};

export default LLMChat;
